import Button from "@components/base/button";
import Loader from "@components/base/loader/Loader";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { countryDetails } from "@core/dictionary/global";
import { captureException } from "@sentry/react";
import { getApiUrl } from "@utils/baseUrl";
import { getIntelUrl } from "@utils/intelUrl";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import Layout from "../../components/layout/layout";
import { CLOUDINARY_INTEL_BASE_URL, ERROR_MSG } from "../../utils/enum";
import CompanySearchCard from "./components/companySearchCard";
import CompanyResultsPanel from "./components/companyResultsPanel";
import BulkUploadPanel from "./bulk-search/upload/BulkUploadPanel";
import { companySearchTabs, datasetTypes, getDefaultDateRange, initialMetrics, sortOptionsCompany } from "./enum";

const CompanySearch = () => {
  const activeTab = companySearchTabs.COMPANY;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchedState, setSearchedState] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [sortField, setSortField] = useState(sortOptionsCompany[0]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [datasetType, setDatasetType] = useState(datasetTypes.DIRECT);
  const [loading, setLoading] = useState(false);
  const [countryFilterOptions, setCountryFilterOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const urlParams = new URLSearchParams(window.location.search);
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const userFirstPlan = creditDetails?.data?.[0] ?? null;
  const currentUserPlan = userFirstPlan?.user_active_plan?.split("_").pop() || "";
  const defaultDateRange = getDefaultDateRange(currentUserPlan);
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";

  const initialFilters = useMemo(
    () => ({
      dateRange: defaultDateRange,
      listedCompany: null,
      jurisdiction: [],
      metrics: initialMetrics,
    }),
    [defaultDateRange]
  );

  const [filters, setFilters] = useState(initialFilters);

  const showDatasetToggle = useMemo(() => {
    if (!creditDetails?.data || !Array.isArray(creditDetails.data)) return false;
    const filteredDataset = creditDetails.data.filter((item) => item?.product_id !== "trademo_MC" && item.navigate !== "upgrade");
    const isMirrorSubscribed = filteredDataset.some((dataset) => dataset?.is_active && countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
    const isDirectSubscribed = filteredDataset.some((dataset) => dataset?.is_active && !countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
    return isDirectSubscribed && isMirrorSubscribed;
  }, [creditDetails]);

  const getSlugHandler = async (companyId, P = "") => {
    try {
      const params = {
        companyId: companyId,
      };
      const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params });
      if (data?.status === "success" && data?.data?.companySlug) {
        trademoTrackingEvents(trackingEventDictionary.companySearchCompanyProfileClick, generateTrackingObject("company-search", window.location.pathname, currentUserPlan));
        const url = getIntelUrl() + data.data.companySlug + "/" + data.data.aliasCompanyId + `?country=${urlParams.get("country") || "multicountry"}&type=${urlParams.get("type")}&tab=I${P || ""}`;
        window.open(url, "_blank");
      } else {
        toast.error("No company profile found in our system !!!");
      }
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    }
  };

  const clearSelections = () => {
    setSelectedRows([]);
  };

  const handleDatasetTypeChange = (e) => {
    const updatedDataset = e.target.checked ? datasetTypes.MIRROR : datasetTypes.DIRECT;
    setDatasetType(updatedDataset);
    resetSearch();
    trademoTrackingEvents(trackingEventDictionary.companySearchDatasetToggle, { ...generateTrackingObject("company-search", window.location.pathname, currentUserPlan), datasetType: updatedDataset });
  };

  const intelMultiCountryCompanySearch = async () => {
    try {
      setLoading(true);
      const payload = {
        companyName: searchedState.search[0].label,
        probableMatchesLimit: 20,
        data_type: datasetType,
      };

      const { data } = await axios.post(getApiUrl("intelMultiCountryCompanySearch"), payload);

      const companyData = data?.companies || [];

      setTableData(companyData);
      setTotalDataCount(data?.totalCompaniesCount || 0);
      const countriesOpt = {};
      companyData.forEach((country) => {
        countriesOpt[country.country_code] = country.country;
      });
      const countryOptionsArray = Object.keys(countriesOpt)
        .map((code) => ({
          country_code: code,
          key: countriesOpt[code],
        }))
        .sort((a, b) => a.key.localeCompare(b.key));
      setCountryFilterOptions(countryOptionsArray);
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [searchedState, filters, sortField]);

  useEffect(() => {
    if (searchedState) {
      intelMultiCountryCompanySearch();
    }
  }, [searchedState]);

  const filteredTableData = useMemo(() => {
    if (!searchedState) return tableData;

    const countryFilter = filters?.jurisdiction || [];
    let filteredData = [...tableData];

    if (countryFilter.length > 0) {
      filteredData = filteredData.filter((data) => countryFilter.includes(data.country));
    }

    if (filters.listedCompany) {
      filteredData = filteredData.filter((data) => {
        const hasTickers = Object.keys(data.stockTickerDetails || {}).length;
        return filters.listedCompany.key === "exclude" ? hasTickers === 0 : hasTickers > 0;
      });
    }

    return filteredData.sort((a, b) => (b[sortField.value] ?? 0) - (a[sortField.value] ?? 0));
  }, [tableData, filters, sortField, searchedState]);

  const handleSearchClick = (searchValues, tradeDirection) => {
    setSelectedRows([]);
    setSelectedRowData(null);
    setFilters(initialFilters);
    setSortField(sortOptionsCompany[0]);
    setTableData([]);
    setTotalDataCount(0);
    setCountryFilterOptions([]);
    setSearchedState({ search: searchValues, tradeDirection, datasetType });
    trademoTrackingEvents(trackingEventDictionary.companySearchSearch, { ...generateTrackingObject("company-search", window.location.pathname, currentUserPlan), activeTab, tradeDirection: tradeDirection?.value });
  };

  const resetSearch = () => {
    trademoTrackingEvents(trackingEventDictionary.companySearchReset, generateTrackingObject("company-search", window.location.pathname, currentUserPlan));
    setTableData([]);
    setSearchedState(null);
    setTotalDataCount(0);
    setSelectedRows([]);
    setSelectedRowData(null);
    setSelectedItems([]);
    setSearchInput("");
    setSortField(sortOptionsCompany[0]);
    setFilters(initialFilters);
    setCountryFilterOptions([]);
  };

  const renderContent = () => {
    const hasActiveFilters = !!filters.listedCompany || filters.jurisdiction.length > 0;

    if (!searchedState) {
      return (
        <div className="tw-text-[15px] tw-gap-3 tw-max-w-56 tw-mx-auto tw-mt-44 tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col">
          <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1759739972/intel/misc/company_1_wzv6lr.svg`} alt="no-search" className="tw-h-32 tw-mx-auto" />
          <div className="tw-text-center tw-mt-4 tw-font-medium">Discover Companies by Name</div>
        </div>
      );
    }

    if (loading && filteredTableData.length === 0) {
      return null;
    }

    if (searchedState && filteredTableData.length === 0 && !loading && !hasActiveFilters) {
      return (
        <div className="tw-text-[15px] tw-gap-3 tw-max-w-72 tw-mx-auto tw-mt-32 tw-leading-[1.43] tw-flex tw-justify-center tw-flex-col">
          <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1759739973/intel/misc/empty_state_nuhcpl.svg`} alt="no-results" className="tw-mx-auto" />
          <div className="tw-text-center tw-mt-4 tw-font-medium">No results found. Try checking the spelling, using fewer words, or different keywords.</div>
        </div>
      );
    }

    return (
      <CompanyResultsPanel
        tableData={filteredTableData}
        rawTableData={tableData}
        totalDataCount={totalDataCount}
        sortField={sortField}
        setSortField={setSortField}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectedRowData={selectedRowData}
        setSelectedRowData={setSelectedRowData}
        clearSelections={clearSelections}
        filters={filters}
        setFilters={setFilters}
        initialFilters={initialFilters}
        hasActiveFilters={hasActiveFilters}
        countryFilterOptions={countryFilterOptions}
        currentUserPlan={currentUserPlan}
        searchedState={searchedState}
        activeTab={activeTab}
        getSlugHandler={getSlugHandler}
        onSortTrack={() => trademoTrackingEvents(trackingEventDictionary.companySearchSortChange, generateTrackingObject("company-search", window.location.pathname, currentUserPlan))}
        onTrackViewContacts={() => trademoTrackingEvents(trackingEventDictionary.companySearchViewContacts, generateTrackingObject("company-search", window.location.pathname, currentUserPlan))}
        onTrackBookmark={() => trademoTrackingEvents(trackingEventDictionary.companySearchBookmark, generateTrackingObject("company-search", window.location.pathname, currentUserPlan))}
      />
    );
  };

  return (
    <Layout type={"companySearch"} pageTitle="Search Companies | Trademo Intel" setNotification={() => null}>
      {loading && <Loader />}
      {!STARTERPLAN && (
        <div className="tw-text-gray-900 tw-mx-auto">
          <div className="tw-flex tw-items-end tw-w-full tw-justify-center tw-gap-3 tw-pb-3 tw-pt-4">
            <div>
              {showDatasetToggle && (
                <div className="tw-flex tw-justify-end tw-px-8 tw-text-sm">
                  <div className="tw-flex tw-items-center tw-font-medium tw-gap-1">
                    <span className={`${datasetType === datasetTypes.DIRECT ? "tw-text-gray-900" : "tw-text-gray-400"} tw-pr-2`}>Direct Datasets</span>
                    <Form.Check type="switch" id="data-set-switch" checked={datasetType === datasetTypes.MIRROR} onChange={handleDatasetTypeChange} className="tw-w-fit !tw-cursor-pointer [&_input:checked+label::before]:!tw-bg-primary-purple-500 [&_input:checked+label::before]:!tw-border-primary-purple-500" />
                    <span className={`${datasetType === datasetTypes.MIRROR ? "tw-text-gray-900" : "tw-text-gray-400"}`}>Mirror Datasets</span>
                  </div>
                </div>
              )}
              <CompanySearchCard activeTab={activeTab} onSearch={handleSearchClick} setSelectedItems={setSelectedItems} selectedItems={selectedItems} searchInput={searchInput} setSearchInput={setSearchInput} resetSearch={resetSearch} />
            </div>

            {searchedState && (
              <div data-for="reset" className="tw-mb-4" data-tip="Clear all searches and filters">
                <Button variant={"secondaryWithIcon"} size={"medium"} className="tw-rounded-[20px]" onClick={resetSearch}>
                  <span className="material-symbols-outlined">find_replace</span>
                  <span className="tw-text-sm tw-font-medium">Reset</span>
                </Button>
                <ReactTooltip className="trademo-tooltip" textColor="#FFFFFF" place="bottom" id="reset" />
              </div>
            )}
          </div>

          {!searchedState ? (
            <div className="tw-mx-auto tw-w-[950px] tw-pb-10">
              <div className="tw-relative tw-my-6 tw-flex tw-items-center tw-justify-center">
                <div className="tw-absolute tw-inset-x-0 tw-top-1/2 tw-h-px tw-bg-gray-200" />
                <span className="tw-relative tw-rounded-full tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-0.5 tw-text-sm tw-text-gray-500">or</span>
              </div>
              <BulkUploadPanel />
            </div>
          ) : (
            renderContent()
          )}
        </div>
      )}
    </Layout>
  );
};

export default CompanySearch;
